import { useState, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useThermalDesign } from "../../../ThermalDesignContext";
import { showToastMessage } from "../../../../../../../utils/commonFunctions";
import { DividerLine } from "../../../SatelliteProfileImgAssets/SatelliteProfileImgAssets";
import { ThermalModalHeader, ThermalModalFooter } from "../components/common";
import { getDefaultNodeValues, useConductanceCellInput } from "../hooks/ThermalDesignerHooks";
import ConfirmationDialog from "../../../../../../CommonComponents/confirmation_dialog/ConfirmationDialog";
import { NodeSpecItem, type NodeValues } from "../../../ThermalDesignTypes";
import { createColumnHelper, useReactTable, flexRender, getCoreRowModel } from "@tanstack/react-table";
import { THERMAL_FACE_BODY_VEC, THERMAL_FACE_ORDER } from "../../../ThermalDesignContext";
import type { ThermalFaceId } from "../../../ThermalDesignTypes";
import {
  exportConductanceMatrixAsJson,
  exportConductanceMatrixAsCsv,
  readFileAsText,
  parseConductanceCsv,
  applyConductanceCsvToNodes,
  applyConductanceJsonToNodes,
} from "../utils/thermalConfigUtils";

const bodyPortal = (children: React.ReactNode) =>
  typeof document !== "undefined" ? createPortal(children, document.body) : null;

// Pure helpers — module-level so they are not recreated on every render
const getExternalFaceIdFromBodyVec = (bodyVec?: number[] | null): ThermalFaceId | null => {
  if (!bodyVec || bodyVec.length !== 3) return null;
  const key = bodyVec.join(",");
  return THERMAL_FACE_ORDER.find((faceId) => THERMAL_FACE_BODY_VEC[faceId].join(",") === key) ?? null;
};

const OPPOSITE_FACE: Record<ThermalFaceId, ThermalFaceId> = {
  "+X": "-X", "-X": "+X",
  "+Y": "-Y", "-Y": "+Y",
  "+Z": "-Z", "-Z": "+Z",
};

const isOppositeExternalFacePair = (a: ThermalFaceId | null, b: ThermalFaceId | null): boolean =>
  !!(a && b && OPPOSITE_FACE[a] === b);

// Module-level: prevents remounting on parent re-renders; blur-only commits keep focus stable.
const ConductanceCell = ({
  initialValue,
  isDisabled,
  onCommit,
}: {
  initialValue: number;
  isDisabled: boolean;
  onCommit?: (value: number) => void;
}) => {
  const { localValue, handleChange, handleBlur } = useConductanceCellInput(initialValue, onCommit);

  return (
    <input
      type="text"
      disabled={isDisabled}
      value={localValue}
      onChange={isDisabled ? undefined : (e) => handleChange(e.target.value)}
      onBlur={isDisabled ? undefined : handleBlur}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
      className={`conductance-input ${isDisabled ? "disabled-input" : ""}`}
    />
  );
};

const getFaceLabel = (node: NodeSpecItem): string => {
  if (node.values?.node_isInternal) return node.id;
  const bodyVec = node.values?.node_body_vec;
  if (!bodyVec || bodyVec.length !== 3) return node.id;
  const key = bodyVec.join(",");
  const match = THERMAL_FACE_ORDER.find((faceId) => THERMAL_FACE_BODY_VEC[faceId].join(",") === key);
  return match ?? node.id;
};

const NodeTrackerItem = ({ node }: { node: NodeSpecItem }) => (
  <div className={`thermal_model_node_specs_tracker_item state ${node.status}`}>
    <span className="nodeSpecIdSpan">{getFaceLabel(node)}</span>
  </div>
);

const ConductanceMatrixModal = () => {
  const { setOpenModal, isSatDesignerSidebarOpen, nodeSpecsOptions, setNodeSpecsOptions } = useThermalDesign();
  const [isModalMinimized, setIsModalMinimized] = useState(false);
  const [showSaveConfirmationDialog, setShowSaveConfirmationDialog] = useState(false);
  const [showDiscardConfirmationDialog, setShowDiscardConfirmationDialog] = useState(false);

  // Snapshot of every node's conductance array taken when the modal first mounts.
  // Used to restore original values if the user cancels without saving.
  const conductanceSnapshot = useRef<Record<string, number[]>>(
    Object.fromEntries(
      nodeSpecsOptions.map((n) => [n.id, [...(n.values?.node_conductance ?? [])]])
    )
  );

  const handleSaveAndExit = () => {
    if (!allNodesConfigured) {
      showToastMessage('info', 'All thermal nodes must be configured before saving the conductance matrix.');
      return;
    }
    setShowSaveConfirmationDialog(true);
  };

  const handleConfirmSave = () => {
    setShowSaveConfirmationDialog(false);
    showToastMessage('success', 'Conductance matrix updated successfully.');
    setOpenModal((prev) => ({
      ...prev,
      isThermalConductivityModalOpen: false,
      isNodeSpecsModalOpen: true,
    }));
  };

  const handleCancelSaveConfirmation = () => {
    setShowSaveConfirmationDialog(false);
  };

  const handleCancel = () => {
    setShowDiscardConfirmationDialog(true);
  };

  const handleConfirmDiscard = () => {
    setNodeSpecsOptions((prev) =>
      prev.map((node) => {
        const original = conductanceSnapshot.current?.[node.id];
        if (!original || !node.values) return node;
        return {
          ...node,
          values: {
            ...node.values,
            node_conductance: [...original],
          },
        };
      })
    );
    setShowDiscardConfirmationDialog(false);
    setOpenModal((prev) => ({
      ...prev,
      isThermalConductivityModalOpen: false,
    }));
  };

  const handleCancelDiscardConfirmation = () => {
    setShowDiscardConfirmationDialog(false);
  };

  const handleExportJson = () => {
    exportConductanceMatrixAsJson(nodeSpecsOptions);
  };

  const handleExportCsv = () => {
    exportConductanceMatrixAsCsv(nodeSpecsOptions);
  };

  const handleImport = async (file: File) => {
    try {
      const text = await readFileAsText(file);
      let updated: typeof nodeSpecsOptions;
      if (file.name.endsWith(".csv")) {
        const matrix = parseConductanceCsv(text);
        updated = applyConductanceCsvToNodes(matrix, nodeSpecsOptions);
      } else {
        const parsed = JSON.parse(text);
        if (Array.isArray(parsed)) {
          updated = parsed;
        } else {
          updated = applyConductanceJsonToNodes(parsed, nodeSpecsOptions);
        }
      }
      setNodeSpecsOptions(updated);
      showToastMessage("success", "Conductance data imported successfully");
    } catch {
      showToastMessage("error", "Import failed: could not parse file");
    }
  };

  const header = {
    title: "Conductance Matrix",
    isModalMinimized,
    setIsModalMinimized,
    setOpenModal,
    modalItem: "isThermalConductivityModalOpen",
    onClose: handleCancel,
    onExportJson: handleExportJson,
    onExportCsv: handleExportCsv,
    onImport: handleImport,
  };

  // Stable key derived from node IDs — changes only when the node structure changes,
  // not when conductance values are edited. This keeps `columns` stable so TanStack
  // Table doesn't rebuild column definitions on every keystroke/blur, which would
  // cause focus loss in the input cells.
  const nodeIdKey = useMemo(() => nodeSpecsOptions.map((n) => n.id).join(','), [nodeSpecsOptions]);

  // All non-unavailable nodes must be configured before the matrix is editable
  const allNodesConfigured = useMemo(
    () => nodeSpecsOptions.filter((n) => n.status !== "unavailable").every((n) => n.status === "complete"),
    [nodeSpecsOptions]
  );

  const columnHelper = createColumnHelper<NodeSpecItem>();


  const columns = useMemo(() => {
    const firstColumn = columnHelper.display({
      id: "rowName",
      header: () => <div className="conductance-header-bold"></div>,
      cell: (item) => (
        <div className="thermal_model_node_specs_tracker_div conductance_table_header">
          <NodeTrackerItem node={item.row.original} />
        </div>
      ),
    });

    // Dynamic conductance columns
    // nodeSpecsOptions is intentionally captured as a snapshot here — columns only
    // recompute when node IDs change (nodeIdKey), not on conductance value edits.
    // Live values are delivered via info.getValue() through the `data` prop of useReactTable.
    const conductanceColumns = nodeSpecsOptions.map((colNode, colIndex) =>
      columnHelper.accessor((row) => row.values?.node_conductance[colIndex] ?? 0, {
        id: colNode.id,
        header: () => (
          <div className="thermal_model_node_specs_tracker_div conductance_table_header main_header">
            <NodeTrackerItem node={colNode} />
          </div>
        ),
        cell: (info) => {
          const rowIndex = info.row.index;
          const isDiagonal = rowIndex === colIndex; // Disable when row === column
          const isLowerTriangle = rowIndex > colIndex;

          const rowNode = info.row.original;
          const colNodeForCell = nodeSpecsOptions[colIndex];
          const rowFaceId = rowNode?.values?.node_isInternal ? null : getExternalFaceIdFromBodyVec(rowNode?.values?.node_body_vec);
          const colFaceId = colNodeForCell?.values?.node_isInternal ? null : getExternalFaceIdFromBodyVec(colNodeForCell?.values?.node_body_vec);

          // Cube opposite faces are not in contact: disable (+X,-X), (+Y,-Y), (+Z,-Z)
          const isOppositeFacePair = isOppositeExternalFacePair(rowFaceId, colFaceId);
          // INT1 and INT2 are not in contact with each other
          const isInternalPair = (rowNode?.values?.node_isInternal ?? false) && (colNodeForCell?.values?.node_isInternal ?? false);
          // Disable diagonal, opposite faces, internal-internal pair, and lower triangle (mirrors upper)
          const isDisabled = isDiagonal || isOppositeFacePair || isInternalPair || isLowerTriangle;

          // commitValue writes symmetrically to both (row,col) and (col,row), so
          // info.getValue() (via the accessor on live `data`) is always correct for
          // both upper and lower triangle cells.
          const displayValue = info.getValue();

          const commitValue = (newValue: number) => {
            setNodeSpecsOptions((prev) => {
              const newData = [...prev];

              const rowId = info.row.original.id;
              const colId = colNodeForCell?.id;
              const targetRowIndex = prev.findIndex((r) => r.id === rowId);
              const targetColIndex = colId ? prev.findIndex((r) => r.id === colId) : -1;

              if (targetRowIndex < 0 || targetColIndex < 0) return prev;

              const rowValues: NodeValues = newData[targetRowIndex].values ?? getDefaultNodeValues(prev.length);
              const colValues: NodeValues = newData[targetColIndex].values ?? getDefaultNodeValues(prev.length);

              const nextRowConductance: number[] = Array.isArray(rowValues.node_conductance)
                ? [...rowValues.node_conductance]
                : Array(prev.length).fill(0);
              const nextColConductance: number[] = Array.isArray(colValues.node_conductance)
                ? [...colValues.node_conductance]
                : Array(prev.length).fill(0);

              // Write symmetric values: (row,col) and (col,row)
              nextRowConductance[colIndex] = newValue;
              nextColConductance[rowIndex] = newValue;

              newData[targetRowIndex] = {
                ...newData[targetRowIndex],
                values: {
                  ...rowValues,
                  node_conductance: nextRowConductance,
                },
              };
              newData[targetColIndex] = {
                ...newData[targetColIndex],
                values: {
                  ...colValues,
                  node_conductance: nextColConductance,
                },
              };

              return newData;
            });
          };

          // ConductanceCell is a module-level component — React reconciles it
          // across column recomputes without remounting, so focus and typed
          // text are preserved when nodeSpecsOptions changes mid-edit.
          return (
            <ConductanceCell
              initialValue={displayValue ?? 0}
              isDisabled={isDisabled}
              onCommit={isDisabled ? undefined : commitValue}
            />
          );
        },
      })
    );

    return [firstColumn, ...conductanceColumns];
  }, [nodeIdKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const table = useReactTable({
    data: nodeSpecsOptions,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className={`nodeSpecsModal ${!isSatDesignerSidebarOpen ? "left" : "right"}`}>
      <ThermalModalHeader header={header} />
      {!isModalMinimized && (
        <>
          <div className="nodeSpecsModal__inputs nodeSpecsModal__inputs--scroll">
            {!allNodesConfigured && (
              <div className="nodespecsModal_warning_blue_div">
                All nodes must be fully configured before the conductance matrix can be saved and submitted.
              </div>
            )}
            <table className="conductance-table">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id}>{flexRender(header.column.columnDef.header, header.getContext())}</th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="conductance-td">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="nodeSpecsModal__divider__div">
            <DividerLine />
          </div>
          <ThermalModalFooter onSave={handleSaveAndExit} isLastNode={true} onCancel={handleCancel} />
        </>
      )}

      {bodyPortal(
        <ConfirmationDialog
          type="update"
          open={showSaveConfirmationDialog}
          title="Confirm Submission"
          description="Your conductance values are ready to be applied. After confirmation, the Surface Configuration panel will open. Review your settings and click 'Save' to finalize and submit the thermal configuration."
          onConfirm={handleConfirmSave}
          onCancel={handleCancelSaveConfirmation}
          confirm_button_text="Submit"
          cancel_button_text="Cancel"
        />
      )}

      {bodyPortal(
        <ConfirmationDialog
          type="discard"
          open={showDiscardConfirmationDialog}
          title="Discard Changes"
          description="Are you sure you want to discard your unsaved changes to the conductance matrix?"
          onConfirm={handleConfirmDiscard}
          onCancel={handleCancelDiscardConfirmation}
          onDiscard={handleCancelDiscardConfirmation}
          confirm_button_text="Discard"
          cancel_button_text="Keep Editing"
        />
      )}
    </div>
  );
};

export default ConductanceMatrixModal;
